// Cart state provider — reads /api/cart/me on boot, exposes add/update/
// remove/refresh + drawer open state.
//
// Contract (PROMPT 2.4 + 2.5):
//   - Boot: GET /api/cart/me → { cartId, totalQuantity, lines, checkoutUrl }.
//   - addLine(variantId, {intent, addedFrom}): POST /api/cart/lines with
//     op:"add" (default). PDP → +1 to the line.
//   - updateQuantity(variantId, qty, attributes): POST /api/cart/lines with
//     op:"set". Replace-quantity semantics on the server.
//   - removeLine(variantId, attributes): POST /api/cart/lines with op:"set"
//     and quantity:0. Server maps to cartLinesRemove.
//   - openDrawer / closeDrawer / drawerOpen — drawer visibility.
//   - NO timer fallback anywhere (spec H2, R8).

import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useAccount } from './account';
import { API_BASE } from '../lib/apiBase';
import {
        isFreebieLine,
        merchSubtotal,
        tierFor,
        freebieHandleForTier,
        resolveFreebie,
        resolvedHandleFor,
        freebieDeclined,
} from '../lib/freebies';

const CartContext = createContext({
        cartId: null,
        totalQuantity: 0,
        lines: [],
        checkoutUrl: null,
        restored: false,
        drawerOpen: false,
        loading: false,
        openDrawer: () => {},
        closeDrawer: () => {},
        consumeRestored: () => {},
        addLine: async () => ({ ok: false }),
        updateQuantity: async () => ({ ok: false }),
        removeLine: async () => ({ ok: false }),
        removeLineById: async () => ({ ok: false }),
        addWarranty: async () => ({ ok: false }),
        refresh: async () => {},
        checkoutAsGuest: async () => ({ ok: false }),
});

const BACKEND = API_BASE;

async function fetchCart(signal) {
        const res = await fetch(`${BACKEND}/api/cart/me`, {
                credentials: 'include',
                signal,
        });
        if (!res.ok) return { cartId: null, totalQuantity: 0, lines: [], checkoutUrl: null };
        const data = await res.json();
        return {
                cartId: data.cartId || null,
                totalQuantity: data.totalQuantity || 0,
                lines: data.lines || [],
                checkoutUrl: data.checkoutUrl || null,
        };
}

async function postLines({ variantId, quantity, attributes, op, lineId }) {
        const res = await fetch(`${BACKEND}/api/cart/lines`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ variantId, quantity, attributes, op, lineId }),
        });
        if (!res.ok) return { ok: false, status: res.status };
        const data = await res.json();
        return { ok: true, ...data };
}

export function CartProvider({ children }) {
        const [cartId, setCartId] = useState(null);
        const [totalQuantity, setTotalQuantity] = useState(0);
        const [lines, setLines] = useState([]);
        const [checkoutUrl, setCheckoutUrl] = useState(null);
        const [restored, setRestored] = useState(false);
        const [drawerOpen, setDrawerOpen] = useState(false);
        const [loading, setLoading] = useState(false);

        const applyCart = useCallback((c) => {
                setCartId(c.cartId || null);
                setTotalQuantity(c.totalQuantity || 0);
                setLines(c.lines || []);
                setCheckoutUrl(c.checkoutUrl || null);
        }, []);

        const load = useCallback(() => {
                const controller = new AbortController();
                setLoading(true);
                fetchCart(controller.signal)
                        .then((c) => {
                                applyCart(c);
                                if (c.cartId && (c.lines || []).length > 0) setRestored(true);
                        })
                        .catch(() => {})
                        .finally(() => setLoading(false));
                return () => controller.abort();
        }, [applyCart]);

        useEffect(() => {
                const stop = load();
                return stop;
        }, [load]);

        const openDrawer = useCallback(() => {
                setDrawerOpen(true);
                // Every open re-reads server-side truth (spec H1).
                load();
        }, [load]);
        const closeDrawer = useCallback(() => setDrawerOpen(false), []);

        const consumeRestored = useCallback(() => setRestored(false), []);

        const addLine = useCallback(async (variantId, { intent = 'buy', addedFrom = 'pdpage.v1' } = {}) => {
                const res = await postLines({
                        variantId,
                        quantity: 1,
                        op: 'add',
                        attributes: [
                                { key: '_intent', value: intent },
                                { key: '_addedFrom', value: addedFrom },
                        ],
                });
                if (res.ok) applyCart(res);
                return res;
        }, [applyCart]);

        const updateQuantity = useCallback(async (variantId, quantity, attributes = []) => {
                const res = await postLines({ variantId, quantity, op: 'set', attributes });
                if (res.ok) applyCart(res);
                return res;
        }, [applyCart]);

        const removeLine = useCallback(async (variantId, attributes = []) => {
                const res = await postLines({ variantId, quantity: 0, op: 'set', attributes });
                if (res.ok) applyCart(res);
                return res;
        }, [applyCart]);

        // Delete a specific cart line by line id (warranty add-ons share one
        // variantId across parents, so variantId-based removal is ambiguous).
        const removeLineById = useCallback(async (lineId) => {
                const res = await postLines({ lineId, op: 'removeById' });
                if (res.ok) applyCart(res);
                return res;
        }, [applyCart]);

        // Warranty add-on — appends the hidden extended-warranty line tied
        // to its parent physical line via the `warranty_of` attribute.
        const addWarranty = useCallback(async (warrantyVariantId, parentLineId) => {
                const res = await postLines({
                        variantId: warrantyVariantId,
                        quantity: 1,
                        op: 'add',
                        attributes: [
                                { key: 'warranty_of', value: String(parentLineId) },
                                { key: '_intent', value: 'warranty' },
                                { key: '_addedFrom', value: 'cartdrawer.warranty.v1' },
                        ],
                });
                if (res.ok) applyCart(res);
                return res;
        }, [applyCart]);

        const refresh = useCallback(async () => {
                const c = await fetchCart(undefined);
                applyCart(c);
        }, [applyCart]);

        // BUG 4 — Anonymous cart must survive sign-in.
        // When the account status transitions guest/loading → authed we
        // call POST /api/cart/merge-on-auth exactly once. Backend binds the
        // guest cart to the customer AND appends any lines from the
        // customer's prior saved cart. Then we refresh so the UI reflects
        // the merged state. One-shot: ref guards against StrictMode double-
        // fire and status re-emits.
        const { status: accountStatus } = useAccount();
        const mergedForAuthRef = useRef(false);
        useEffect(() => {
                if (accountStatus !== 'authed') {
                        if (accountStatus === 'guest') mergedForAuthRef.current = false;
                        return undefined;
                }
                if (mergedForAuthRef.current) return undefined;
                mergedForAuthRef.current = true;
                let cancelled = false;
                (async () => {
                        try {
                                await fetch(`${BACKEND}/api/cart/merge-on-auth`, {
                                        method: 'POST',
                                        credentials: 'include',
                                });
                        } catch { /* non-critical */ }
                        if (!cancelled) await refresh();
                })();
                return () => { cancelled = true; };
        }, [accountStatus, refresh]);

        const checkoutAsGuest = useCallback(async () => {
                const res = await fetch(`${BACKEND}/api/cart/checkout-as-guest`, {
                        method: 'POST',
                        credentials: 'include',
                });
                if (!res.ok) return { ok: false, status: res.status };
                const data = await res.json();
                return { ok: true, ...data };
        }, []);

        // Free-gift auto-sync (freebie progress tiers). Watches the
        // merchandise subtotal: adds the tier gift once earned, swaps it on
        // tier change, removes it when the cart drops below the tier. A
        // gift the shopper manually removes is declined for the session and
        // never re-added. Busy-ref guards StrictMode double-fire and
        // re-entrancy while a mutation is in flight.
        const freebieBusyRef = useRef(false);
        useEffect(() => {
                if (freebieBusyRef.current) return undefined;
                const tier = tierFor(merchSubtotal(lines));
                const wantHandle = tier > 0 ? freebieHandleForTier(tier, lines) : null;
                const resolvedWant = wantHandle ? resolvedHandleFor(wantHandle) : null;
                const existing = (lines || []).filter(isFreebieLine);
                const stale = existing.filter(
                        (l) => !resolvedWant || (l?.merchandise?.product?.handle || '') !== resolvedWant
                );
                const earned = Boolean(
                        resolvedWant &&
                        existing.some((l) => (l?.merchandise?.product?.handle || '') === resolvedWant)
                );
                if (!stale.length && (earned || !wantHandle)) return undefined;
                let cancelled = false;
                freebieBusyRef.current = true;
                (async () => {
                        try {
                                for (const l of stale) {
                                        // eslint-disable-next-line no-await-in-loop
                                        const res = await postLines({ lineId: l.id, op: 'removeById' });
                                        if (res.ok) applyCart(res);
                                }
                                if (!cancelled && wantHandle && !earned) {
                                        const resolution = await resolveFreebie(wantHandle);
                                        if (resolution && !freebieDeclined(resolution.handle)) {
                                                const res = await postLines({
                                                        variantId: resolution.variantId,
                                                        quantity: 1,
                                                        op: 'add',
                                                        attributes: [
                                                                { key: '_intent', value: 'freebie' },
                                                                { key: '_freebie_tier', value: String(tier) },
                                                                { key: '_addedFrom', value: 'cart.freebie.v1' },
                                                        ],
                                                });
                                                if (res.ok) applyCart(res);
                                        }
                                }
                        } catch { /* non-critical — next cart change retries */ }
                        freebieBusyRef.current = false;
                })();
                return () => { cancelled = true; };
        }, [lines, applyCart]);

        return (
                <CartContext.Provider
                        value={{
                                cartId, totalQuantity, lines, checkoutUrl, restored, loading,
                                drawerOpen, openDrawer, closeDrawer, consumeRestored,
                                addLine, updateQuantity, removeLine, removeLineById, addWarranty, refresh, checkoutAsGuest,
                        }}
                >
                        {children}
                </CartContext.Provider>
        );
}

export function useCart() {
        return useContext(CartContext);
}
